import React, { useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../auth/AuthContext';
import { useIssuedBeritaAcaraWorkOrderIds, useSurveyTeamsScheduleQuery } from '../../../../query/hooks';
import {
  applyBeritaAcaraIssuedFlag,
  buildQuotationScheduleDaySummariesByDate,
  buildWorkOrderDocIdBySchedule,
  MonthlyQuotationTeamCalendar,
  QuotationScheduleDayDetailDialog,
  SurveyScheduleCalendarToolbar,
  surveyTeamsForPetugasCalendarView,
  type QuotationScheduleDaySummary,
} from '../../../../components/surveyScheduleCalendar';
import { currentMonthAnchor } from '../../../../sampling/surveyScheduleCalendar';
import { buildSamplingScheduleGridRows, SamplingScheduleGridList } from '../GridList';

export function SamplingCalendarTabScreen({
  defaultUserId = null,
  viewMode = 'calendar',
}: {
  defaultUserId?: number | null;
  viewMode?: 'calendar' | 'grid-list';
}) {
  const { jwt } = useAuth();
  const navigate = useNavigate();
  const [monthAnchor, setMonthAnchor] = useState(() => currentMonthAnchor());
  const [detailDialog, setDetailDialog] = useState<{ date: string; summary: QuotationScheduleDaySummary } | null>(null);
  const [detailRefreshing, setDetailRefreshing] = useState(false);
  const scheduleQ = useSurveyTeamsScheduleQuery(jwt, { userNumericId: null });
  const issuedQ = useIssuedBeritaAcaraWorkOrderIds(jwt);

  const calendarTeams = useMemo(() => {
    const allTeams = scheduleQ.data ?? [];
    if (defaultUserId == null) return allTeams;
    return surveyTeamsForPetugasCalendarView(allTeams, defaultUserId);
  }, [scheduleQ.data, defaultUserId]);

  const workOrderDocIdBySchedule = useMemo(
    () => buildWorkOrderDocIdBySchedule(calendarTeams),
    [calendarTeams],
  );
  const summariesByDate = useMemo(() => {
    const base = buildQuotationScheduleDaySummariesByDate(calendarTeams);
    if (!issuedQ.issuedWorkOrderIds.size) return base;
    const withIssued = new Map<string, QuotationScheduleDaySummary[]>();
    for (const [date, rows] of base.entries()) {
      withIssued.set(
        date,
        rows.map((row) => applyBeritaAcaraIssuedFlag(row, issuedQ.issuedWorkOrderIds)),
      );
    }
    return withIssued;
  }, [calendarTeams, issuedQ.issuedWorkOrderIds]);

  const gridRows = useMemo(() => buildSamplingScheduleGridRows(summariesByDate), [summariesByDate]);

  const detailSummary = useMemo(() => {
    if (!detailDialog) return null;
    return (
      (summariesByDate.get(detailDialog.date) ?? []).find(
        (row) => row.scheduleDocumentId === detailDialog.summary.scheduleDocumentId,
      ) ?? detailDialog.summary
    );
  }, [detailDialog, summariesByDate]);

  const detailWorkOrderDocId =
    (detailSummary
      ? workOrderDocIdBySchedule.get(detailSummary.scheduleDocumentId)
      : null) ||
    String(detailSummary?.workOrderDocumentId ?? '').trim() ||
    null;
  const detailJobNumber = String(detailSummary?.workOrderJobNumber ?? '').trim();
  const samplingDetailReady = Boolean(detailWorkOrderDocId && detailJobNumber);

  async function refreshScheduleDetail() {
    if (detailRefreshing) return;
    setDetailRefreshing(true);
    try {
      await Promise.all([scheduleQ.refetch(), issuedQ.refetch()]);
    } finally {
      setDetailRefreshing(false);
    }
  }

  if (scheduleQ.isPending || issuedQ.isPending) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (scheduleQ.isError) {
    return (
      <Alert severity="error">
        {scheduleQ.error instanceof Error
          ? scheduleQ.error.message
          : viewMode === 'grid-list'
            ? 'Gagal memuat daftar penjadwalan.'
            : 'Gagal memuat kalender sampling.'}
      </Alert>
    );
  }

  if (viewMode === 'grid-list') {
    return gridRows.length === 0 ? (
      <Alert severity="info">Belum ada jadwal sampling untuk ditampilkan.</Alert>
    ) : (
      <SamplingScheduleGridList rows={gridRows} />
    );
  }

  return (
    <Stack spacing={2}>
      <SurveyScheduleCalendarToolbar
        viewMode="monthly"
        onViewModeChange={() => undefined}
        weekStart=""
        onWeekStartChange={() => undefined}
        monthAnchor={monthAnchor}
        onMonthAnchorChange={setMonthAnchor}
        weekDates={[]}
        hideViewModeToggle
      />
      {calendarTeams.length === 0 ? (
        <Alert severity="info">Belum ada jadwal sampling untuk ditampilkan.</Alert>
      ) : (
        <MonthlyQuotationTeamCalendar
          monthAnchor={monthAnchor}
          teams={calendarTeams}
          currentScheduleDocumentId=""
          cardTone="detail"
          useScheduleStatusColors={false}
          issuedWorkOrderIds={issuedQ.issuedWorkOrderIds}
          locked
          onQuotationClick={(_, summary) => {
            setDetailDialog({ date: _, summary });
          }}
        />
      )}
      {detailDialog ? (
        <QuotationScheduleDayDetailDialog
          open
          date={detailDialog.date}
          summary={detailSummary}
          onClose={() => setDetailDialog(null)}
          onRefresh={() => void refreshScheduleDetail()}
          refreshing={detailRefreshing}
          actionLabel="Detail sampling"
          actionTestId="sampling-calendar-open-detail"
          actionDisabled={!samplingDetailReady}
          actionDisabledHint={
            !detailWorkOrderDocId
              ? 'Work order belum dibuat untuk penjadwalan ini.'
              : 'Menunggu persetujuan admin sampling. Nomor job belum terbit.'
          }
          onAction={() => {
            if (!detailWorkOrderDocId || !detailJobNumber) return;
            navigate(`/sampling/sampling/${encodeURIComponent(detailWorkOrderDocId)}#detail`);
          }}
        />
      ) : null}
    </Stack>
  );
}
